import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Alert, Typography } from '@mui/material';
import api from '../api';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';
import ReportCardView, { ReportCardDetails } from '../components/ReportCardView';

const SchoolReportCardPreview: React.FC = () => {
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId: string }>();
  const { school, loading } = useSchool();
  const [details, setDetails] = useState<ReportCardDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getSchoolLogoSource = useCallback(() => {
    if (school?.logoUrl) return school.logoUrl;
    const logoUpload = school?.logoUpload;
    const logoData = logoUpload?.data;
    if (logoUpload?.contentType && logoData) {
      if (typeof logoData === 'string') {
        return `data:${logoUpload.contentType};base64,${logoData}`;
      }
      const nestedData = typeof logoData === 'object' && logoData !== null && 'data' in logoData ? (logoData as any).data : [];
      if (Array.isArray(nestedData)) {
        const bytes = Uint8Array.from(nestedData);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        return `data:${logoUpload.contentType};base64,${window.btoa(binary)}`;
      }
    }
    return '';
  }, [school]);

  useEffect(() => {
    const fetchReportCard = async () => {
      if (!school?._id || !studentId) return;
      setLoadingDetails(true);
      setError(null);
      try {
        const response = await api.get(`/schools/${school._id}/term-submissions/student/${studentId}`);
        const payload = response.data;
        const schoolLogoUrl = getSchoolLogoSource() || payload.schoolLogoUrl || '';
        const reportCard: ReportCardDetails = {
          ...payload,
          schoolName: school?.name || payload.schoolName,
          schoolLogoUrl,
        };
        setDetails(reportCard);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load report card details');
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchReportCard();
  }, [school?._id, studentId, school?.name, getSchoolLogoSource]);

  if (loading || loadingDetails) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .report-card-print-area,
          .report-card-print-area * {
            visibility: visible;
          }
          .report-card-print-area {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
          }
          .hide-on-print {
            display: none !important;
          }
        }
      `}</style>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Report Card Preview
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/school/report-cards')}>
          Back to Report Cards
        </Button>
      </Box>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : details ? (
        <Box className="report-card-print-area">
          <ReportCardView details={details} />
        </Box>
      ) : (
        <Alert severity="info">Report card information is not available yet.</Alert>
      )}
    </Box>
  );
};

export default SchoolReportCardPreview;
